import { TranslateService } from '@ngx-translate/core';
import { BasePlugin, Meta, UppyFile } from '@uppy/core';

import { FileUploadOptionsModel } from '../../models/interfaces/file-upload.model';


type DimensionValidatorOptions = NonNullable<FileUploadOptionsModel['imageEditor']>['dimensionValidator'] & {
    id?: string;
};

export default class DimensionValidator extends BasePlugin<DimensionValidatorOptions, Meta, Record<string, never>> {
    static VERSION = '1.0.0';
    opts: DimensionValidatorOptions;
    CONSTANTS = {
        EDITOR_TIMEOUT_MS: 300,
        TIMEOUT_MS: 1000,
        INFO_MSG_TIMEOUT: 5000
    };
    translateService: TranslateService | undefined;

    constructor(uppy: any, options: DimensionValidatorOptions) {
        super(uppy, options);
        this.type = 'modifier';
        this.id = 'DimensionValidator';
        this.opts = options;
        this.translateService = options.translateService;
    }

    install(): void {
        this.uppy.on('file-added', this.handleFileAdded);
        this.uppy.addPreProcessor(this.handlePreProcessor);
    }

    uninstall(): void {
        this.uppy.off('file-added', this.handleFileAdded);
        this.uppy.removePreProcessor(this.handlePreProcessor);
    }

    private handleFileAdded = async(file: UppyFile<Meta, Record<string, never>>): Promise<void> => {
        if (file.data.type.startsWith('image/')) {
            await this.validateDimensions(file, this.opts);
        }
    };

    private handlePreProcessor = async(fileIDs: any): Promise<void> => {
        const validationResults = await Promise.all(
            fileIDs.map(async(fileId: string) => {
                const file = this.uppy.getFile(fileId);
                return await this.validateDimensions(file, this.opts);
            })
        );

        const hasInvalidFiles = validationResults.some((result: any) => !result);
        if (hasInvalidFiles) {
            const dimensions = `${this.opts.requiredWidth}×${this.opts.requiredHeight}px`;
            throw new Error(`Files must meet dimension (${dimensions}) requirements before upload`);
        }
    };

    private async validateDimensions(file: UppyFile<Meta, Record<string, never>>, options: DimensionValidatorOptions): Promise<boolean> {
        const image = await this.loadImageFromFile(file.data as File);
        const isValid = image.width === options.requiredWidth && image.height === options.requiredHeight;
        this.executeValidatorOptions(isValid, options, image, file);

        return isValid;
    }

    private executeValidatorOptions(isValid: boolean, options: DimensionValidatorOptions, image: HTMLImageElement, file: UppyFile<Meta, Record<string, never>>) {
        if (!isValid) {
            let isBiggerImage = true;
            if (options.showValidationMessages) {
                let message = '';
                const dimensions = `${options?.requiredWidth}×${options?.requiredHeight}px`;
                if (image.width < options?.requiredWidth || image.height < options?.requiredHeight) {
                    isBiggerImage = false;
                    message = this.translateService?.instant('FileUploadDimensionValidatorTooSmall', { filename: file.name, dimensions }) ?? '';
                } else {
                    message = this.translateService?.instant('FileUploadDimensionValidatorTooBig', { filename: file.name, dimensions }) ?? '';
                }
                this.showValidationMessage(message);
            }
            if (options.autoOpenEditor && isBiggerImage) {
                this.openImageEditor(file);
            }
        }
    }

    private showValidationMessage(message: string) {
        setTimeout(() => {
            this.uppy.info(message, 'info', this.CONSTANTS.INFO_MSG_TIMEOUT);
        }, this.CONSTANTS.TIMEOUT_MS);
    }

    private openImageEditor(file: UppyFile<Meta, Record<string, never>>) {
        setTimeout(() => {
        // Properly escape the file ID for use in CSS selector
            const escapedFileId = CSS.escape(file.id);
            const editFile = document
                .querySelector(`#uppy_${escapedFileId} .uppy-Dashboard-Item-action--edit`) as HTMLButtonElement;
            if (editFile) {
                editFile.click();
            }
        }, this.CONSTANTS.EDITOR_TIMEOUT_MS);
    }

    private loadImageFromFile(file: File): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            const objectUrl = URL.createObjectURL(file);
            image.onload = () => {
                // Clean up the object URL to free memory
                URL.revokeObjectURL(objectUrl);
                resolve(image);
            };
            image.onerror = () => {
                // Clean up the object URL even on error
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Failed to load image from file'));
            };
            image.src = objectUrl;
        });
    }
}
